import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Table, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap'
import client from '../../api/client'

function EmailTemplates() {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [editing, setEditing] = useState(null)
  const [editForm, setEditForm] = useState({})

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    setLoading(true)
    try {
      const response = await client.get('/admin/api/v1/mailing/templates')
      setTemplates(response.data.templates || response.data || [])
    } catch (err) {
      console.error('Error fetching templates:', err)
      setError('Erreur lors du chargement des templates')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (template) => {
    setEditing(template.email_type)
    setEditForm({
      sendgrid_template_id: template.sendgrid_template_id,
      subject_template: template.subject_template,
      enabled: template.enabled
    })
  }

  const handleCancelEdit = () => {
    setEditing(null)
    setEditForm({})
  }

  const handleSave = async (emailType) => {
    try {
      await client.put(`/admin/api/v1/mailing/templates/${emailType}`, editForm)
      setSuccess(`Template ${emailType} mis à jour`)
      setEditing(null)
      setEditForm({})
      fetchTemplates()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Error updating template:', err)
      setError('Erreur lors de la mise à jour du template')
      setTimeout(() => setError(''), 3000)
    }
  }

  const handleToggleEnabled = async (template) => {
    try {
      await client.put(`/admin/api/v1/mailing/templates/${template.email_type}`, {
        sendgrid_template_id: template.sendgrid_template_id,
        subject_template: template.subject_template,
        enabled: !template.enabled
      })
      setSuccess(`Template ${template.email_type} ${!template.enabled ? 'activé' : 'désactivé'}`)
      fetchTemplates()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Error toggling template:', err)
      setError('Erreur lors de la mise à jour du template')
      setTimeout(() => setError(''), 3000)
    }
  }

  const getTypeLabel = (type) => {
    const labels = {
      verification: 'Vérification email',
      magic_link: 'Magic link',
      password_reset: 'Réinitialisation mot de passe',
      password_change: 'Confirmation changement',
      purchase_confirmation: 'Confirmation achat',
      renewal_reminder: 'Rappel renouvellement',
      win_back: 'Réactivation',
      newsletter: 'Newsletter',
      ticket_created: 'Ticket créé',
      ticket_reply: 'Réponse ticket',
      ticket_status: 'Statut ticket'
    }
    return labels[type] || type
  }

  if (loading) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>📧 Templates Email</h2>
          <p className="text-muted">Configuration des templates SendGrid Dynamic Templates</p>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Type</th>
                <th>Template ID</th>
                <th>Sujet</th>
                <th>Description</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.email_type}>
                  <td>
                    <strong>{getTypeLabel(template.email_type)}</strong>
                    <br />
                    <small className="text-muted">{template.email_type}</small>
                  </td>
                  <td>
                    {editing === template.email_type ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={editForm.sendgrid_template_id}
                        onChange={(e) => setEditForm({ ...editForm, sendgrid_template_id: e.target.value })}
                        placeholder="d-xxxxxxxxxxxxx"
                      />
                    ) : (
                      <code>{template.sendgrid_template_id}</code>
                    )}
                  </td>
                  <td>
                    {editing === template.email_type ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={editForm.subject_template}
                        onChange={(e) => setEditForm({ ...editForm, subject_template: e.target.value })}
                        placeholder="Sujet de l'email"
                      />
                    ) : (
                      template.subject_template
                    )}
                  </td>
                  <td>
                    <small className="text-muted">{template.description}</small>
                  </td>
                  <td>
                    {editing === template.email_type ? (
                      <Form.Check
                        type="switch"
                        checked={editForm.enabled}
                        onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                        label={editForm.enabled ? 'Activé' : 'Désactivé'}
                      />
                    ) : (
                      <Badge bg={template.enabled ? 'success' : 'secondary'}>
                        {template.enabled ? 'Activé' : 'Désactivé'}
                      </Badge>
                    )}
                  </td>
                  <td>
                    {editing === template.email_type ? (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleSave(template.email_type)}
                          className="me-2"
                        >
                          Enregistrer
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleCancelEdit}
                        >
                          Annuler
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(template)}
                          className="me-2"
                        >
                          Modifier
                        </Button>
                        <Button
                          variant={template.enabled ? 'outline-secondary' : 'outline-success'}
                          size="sm"
                          onClick={() => handleToggleEnabled(template)}
                        >
                          {template.enabled ? 'Désactiver' : 'Activer'}
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {templates.length === 0 && (
            <div className="text-center py-4 text-muted">
              Aucun template configuré
            </div>
          )}
        </Card.Body>
      </Card>

      <Row className="mt-3">
        <Col>
          <Alert variant="info">
            <strong>ℹ️ Configuration SendGrid</strong>
            <p className="mb-0 mt-2">
              Les templates doivent être créés dans le dashboard SendGrid (Dynamic Templates).
              Copiez le Template ID (format: d-xxxxxxxxxxxxx) et configurez-le ici pour chaque type d'email.
            </p>
          </Alert>
        </Col>
      </Row>
    </Container>
  )
}

export default EmailTemplates
